import {
  ActionIcon,
  Button,
  ButtonVariant,
  Checkbox,
  Divider,
  Drawer,
  Flex,
  Group,
  MantineColor,
  Menu,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core'
import classes from './DataGrid.module.css'
import {
  IconArrowsSort,
  IconCaretDownFilled,
  IconChevronDown,
  IconColumns3,
  IconDots,
  IconFilterPlus,
  IconGripVertical,
  IconInfoCircle,
  IconPin,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react'
import {
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from 'react'
import { DataGridFilter } from './DataGridFilter'
import { FILTER_MODIFIERS } from '@constants/filterModifiers'
import { StageList, StageListItem } from '@components/StageList/StageList'

export interface DataGridProps {
  data: any[]
  columns: DataGridColumns[]
  sections: DataGridSections[]
  actions: DataGridActions[]
  page: number
  results: number
  selectedActions?: DataGridSelectedActions[]
  buttons?: ReactNode | ReactNode[]
  stages?: StageListItem[]
}

export interface DataGridColumns {
  label: string
  prop: string
  unsortable?: boolean
  unfilterable?: boolean
  defaultSort?: boolean
  pinned?: boolean
  centered?: boolean
}

export interface DataGridSections {
  label: string
  href: string
  default?: boolean
}

export interface DataGridActions {
  label: string
  color?: MantineColor
  onClick: (el: any) => void
}

export interface DataGridFilter {
  column: string
  value: string
  modifier: string
}

export interface DataGridSelectedActions {
  label: string
  color?: MantineColor
  variant?: ButtonVariant
  section?: string
  component?: ReactNode
  onClick: (els: any) => void
}

export const DataGrid = (props: DataGridProps) => {
  const {
    data,
    columns,
    sections,
    actions,
    buttons,
    page,
    selectedActions,
    stages,
    results,
  } = props

  // States
  const [selectedAll, setSelectedAll] = useState<boolean>(false)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [activeSection, setActiveSection] = useState<string>()
  const [activeStage, setActiveStage] = useState<string>(
    stages ? stages[0].name : '',
  )
  const [sortMethod, setSortMethod] = useState<string>()
  const [sortDirection, setSortDirection] = useState<string>('ASC')
  const [filterOpened, setFilterOpened] = useState<boolean>(false)
  const [filterColumn, setFilterColumn] = useState<string | null>()
  const [filterModifier, setFilterModifier] = useState<string | null>('IS')
  const [filterValue, setFilterValue] = useState('')
  const [filter, setFilter] = useState<DataGridFilter | null>(null)
  const [drawerOpened, setDrawerOpened] = useState<boolean>(false)
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    columns.map((curr) => curr.label),
  )

  // Effects
  useEffect(() => {
    const defaultSection = sections.find((curr) => curr.default)
      ?.label as string

    const defaultSort = columns.find((curr) => curr.defaultSort)
      ?.label as string

    setActiveSection(defaultSection)
    setSortMethod(defaultSort)
  }, [sections, columns])

  // Rows
  const rows = data.map((item) => (
    <Table.Tr
      key={item.id}
      bg={
        selectedRows.includes(item.id)
          ? 'var(--mantine-color-blue-light)'
          : undefined
      }
    >
      <Table.Td w={20}>
        <Checkbox
          size="xs"
          aria-label="Select row"
          checked={selectedRows.includes(item.id)}
          onChange={(event) =>
            setSelectedRows(
              event.currentTarget.checked
                ? [...selectedRows, item.id]
                : selectedRows.filter((id: string) => id !== item.id),
            )
          }
        />
      </Table.Td>
      {columns.map(
        (col) =>
          visibleColumns.includes(col.label) && (
            <Table.Td
              key={`${col.prop}-${item.id}`}
              style={{ whiteSpace: 'pre' }}
              align={col.centered ? 'center' : 'left'}
            >
              {item[col.prop]}
            </Table.Td>
          ),
      )}
      <Table.Td>
        <Menu>
          <Menu.Target>
            <ActionIcon variant="default" radius="xl" aria-label="Options">
              <IconDots style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {actions.map((curr, i) => (
              <Menu.Item
                key={`actions-${i}-${item.id}`}
                color={curr.color ? curr.color : 'dark'}
                onClick={() => curr.onClick(item)}
              >
                {curr.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  ))

  // Inner components
  const VisibleColumn = ({ label, pinned = false }: DataGridColumns) => {
    const visible = visibleColumns.includes(label)

    return (
      <Group key={`colvisible-${label}`} justify="space-between">
        <Group gap="xs">
          <ThemeIcon variant="white" color="dark">
            {pinned ? <IconPin size={22} /> : <IconGripVertical size={22} />}
          </ThemeIcon>

          <Text>{label}</Text>
        </Group>
        {!pinned && (
          <Button
            variant={visible ? 'default' : 'outline'}
            color="blue"
            onClick={() => handleToggleColumnVisibility(label)}
          >
            {visible ? 'Hide' : 'Show'}
          </Button>
        )}
      </Group>
    )
  }

  // Handlers
  const handleToggleSelectAll = () => {
    setSelectedAll(!selectedAll)
    !selectedAll
      ? setSelectedRows(data.map((curr) => curr.id))
      : setSelectedRows([])
  }

  const handleDrawerOpen = () => setDrawerOpened(true)
  const handleDrawerClose = () => setDrawerOpened(false)

  const handleSetFilter = () => {
    setFilter({
      column: filterColumn ?? '',
      modifier: filterModifier ?? '',
      value: filterValue ?? '',
    })

    setFilterOpened(false)
  }

  const handleClearFilter = () => {
    setFilter(null)
  }

  const handleToggleColumnVisibility = (label: string) => {
    if (visibleColumns.includes(label)) {
      const newCols = visibleColumns.filter((curr) => curr !== label)
      setVisibleColumns(newCols)

      sortMethod === label &&
        setSortMethod(columns.find((curr) => curr.defaultSort)?.label as string)
    } else {
      setVisibleColumns([label, ...visibleColumns])
    }
  }

  const handleStageChange = (stage: string) => {
    setActiveStage(stage)
  }

  const handleToggleSortDirection = () => {
    sortDirection === 'ASC' ? setSortDirection('DESC') : setSortDirection('ASC')
  }

  return (
    <ScrollArea>
      {stages && (
        <StageList
          stages={stages}
          activeStage={activeStage}
          onClick={handleStageChange}
        />
      )}
      <Group justify="space-between" p="md">
        {sections.length > 1 ? (
          <Menu position="bottom-start">
            <Menu.Target>
              <Group
                className={classes.DataGrid__select}
                gap={3}
                align="center"
              >
                <Title order={4}>{activeSection}</Title>
                <IconCaretDownFilled size={18} />
              </Group>
            </Menu.Target>
            <Menu.Dropdown w={200}>
              {sections.map((curr, i) => (
                <Menu.Item
                  key={`sections-${i}`}
                  onClick={() => setActiveSection(curr.label)}
                >
                  <Text
                    size="md"
                    fw={activeSection === curr.label ? 'bold' : 'normal'}
                  >
                    {curr.label}
                  </Text>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Title order={4}>{activeSection}</Title>
        )}
        {selectedRows.length > 0 ? (
          <Text c="dimmed" size="sm">
            Selected {selectedRows.length} of {results} results
          </Text>
        ) : (
          <Text c="dimmed" size="sm">
            {results} results
          </Text>
        )}
      </Group>
      <Flex justify="space-between" px="md" wrap="nowrap">
        {!selectedActions || (selectedActions && selectedRows.length === 0) ? (
          <Group gap="xs" wrap="nowrap" mr="xs">
            <TextInput
              miw={275}
              style={{ flexShrink: 1 }}
              leftSection={<IconSearch size={22} />}
              leftSectionProps={{
                style: {
                  color: 'var(--mantine-color-dark-2)',
                },
              }}
              rightSection={<IconInfoCircle size={18} />}
              rightSectionProps={{
                style: {
                  color: 'var(--mantine-color-dark-3)',
                  cursor: 'pointer',
                },
              }}
              placeholder="Search"
            />
            <Tooltip label="Add Filter">
              <div>
                <Menu
                  opened={filterOpened}
                  shadow="lg"
                  closeOnItemClick={false}
                  closeOnClickOutside={false}
                  onChange={setFilterOpened}
                >
                  <Menu.Target>
                    <ActionIcon variant="default" h="100%" size="lg" c="dark.3">
                      <IconFilterPlus size={22} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Stack p="md">
                      <Select
                        placeholder="column"
                        data={columns
                          .filter((curr) => visibleColumns.includes(curr.label))
                          .map((curr) => ({
                            label: curr.label,
                            value: curr.label,
                          }))}
                        value={filterColumn}
                        onChange={setFilterColumn}
                        searchable
                      />
                      <Select
                        data={FILTER_MODIFIERS}
                        value={filterModifier}
                        onChange={setFilterModifier}
                      />
                      <TextInput
                        placeholder="Filter Value"
                        value={filterValue}
                        onChange={(event) =>
                          setFilterValue(event.currentTarget.value)
                        }
                      />
                      <Group justify="flex-end">
                        <Button onClick={handleSetFilter}>Apply Filter</Button>
                      </Group>
                    </Stack>
                  </Menu.Dropdown>
                </Menu>
              </div>
            </Tooltip>
          </Group>
        ) : (
          <Group gap="xs" wrap="nowrap" mr="xs">
            {selectedActions.map((action, i) =>
              action.component
                ? cloneElement(action.component as any, {
                    onClick: () => action.onClick(data),
                  })
                : (!action.section || action.section === activeSection) && (
                    <Button
                      key={`selectedActions-${i}`}
                      variant={action.variant ?? 'default'}
                      color={action.color}
                      onClick={() => action.onClick(data)}
                    >
                      {action.label}
                    </Button>
                  ),
            )}
          </Group>
        )}

        <Group gap="xs" wrap="nowrap">
          <Menu position="bottom-start">
            <Menu.Target>
              <Tooltip label="Sort by Column">
                <Flex
                  className={classes.DataGrid__sort}
                  align="center"
                  pl={5}
                  py={1}
                  style={{ flexShrink: 0 }}
                >
                  <ThemeIcon variant="transparent" c="dark.3" mr={1}>
                    <IconArrowsSort size={22} />
                  </ThemeIcon>
                  <Text size="sm" c="dark.3">
                    {sortMethod}
                  </Text>
                  <ThemeIcon variant="transparent" c="dark.3">
                    <IconChevronDown size={22} />
                  </ThemeIcon>
                </Flex>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown w={200}>
              {columns.map(
                (curr, i) =>
                  !curr.unsortable &&
                  visibleColumns.includes(curr.label) && (
                    <Menu.Item
                      key={`sorts-${i}`}
                      onClick={() => setSortMethod(curr.label)}
                    >
                      <Text
                        size="sm"
                        fw={sortMethod === curr.label ? 'bold' : 'normal'}
                      >
                        {curr.label}
                      </Text>
                    </Menu.Item>
                  ),
              )}
            </Menu.Dropdown>
          </Menu>
          <Tooltip
            label={`Sort ${
              sortDirection === 'ASC' ? 'Ascending' : 'Descending'
            }`}
          >
            <ActionIcon
              variant="default"
              h="100%"
              size="lg"
              c="dark.3"
              onClick={handleToggleSortDirection}
            >
              {sortDirection === 'ASC' ? (
                <IconSortAscending size={22} />
              ) : (
                <IconSortDescending size={22} />
              )}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Column Visibility">
            <ActionIcon
              variant="default"
              h="100%"
              size="lg"
              c="dark.3"
              onClick={handleDrawerOpen}
            >
              <IconColumns3 size={22} />
            </ActionIcon>
          </Tooltip>
          {buttons}
        </Group>
      </Flex>
      <Group px="md" mt="xs" gap="xs">
        {filter !== null && (
          <DataGridFilter {...filter} clearFilter={handleClearFilter} />
        )}
      </Group>
      <Table withRowBorders={false} verticalSpacing="xs" mt="xs" striped>
        <Table.Thead>
          <Table.Tr className={classes.DataGrid__thead}>
            <Table.Th w={30}>
              <Checkbox
                size="xs"
                aria-label="Select row"
                checked={selectedAll}
                onChange={handleToggleSelectAll}
              />
            </Table.Th>
            {columns.map(
              (curr, i) =>
                visibleColumns.includes(curr.label) && (
                  <Table.Th key={`columns-${i}`} style={{ whiteSpace: 'pre' }}>
                    <Text size="xs" c="black">
                      {curr.label}
                    </Text>
                  </Table.Th>
                ),
            )}
            <Table.Th w={30} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
      <Flex className={classes.DataGrid__pagination} p="xs" justify="center">
        <Pagination total={10} size="md" defaultValue={page} withEdges />
      </Flex>

      <Drawer
        opened={drawerOpened}
        onClose={handleDrawerClose}
        title={<Title order={4}>Manage Columns</Title>}
        position="right"
      >
        <Stack gap="xs">
          {columns
            .filter((curr) => curr.pinned)
            .map((curr, i) => (
              <VisibleColumn key={`visibleColumnPinned-${i}`} {...curr} />
            ))}
          <Divider my="sm" />
          {columns
            .filter((curr) => !curr.pinned)
            .map((curr, i) => (
              <VisibleColumn key={`visibleColumn-${i}`} {...curr} />
            ))}
        </Stack>
      </Drawer>
    </ScrollArea>
  )
}
